import React, { useEffect, useState } from 'react';
import {
    Alert,
    Card,
    Col,
    Empty,
    InputNumber,
    Row,
    Select,
    Skeleton,
    Space,
    Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';

const { Text, Paragraph } = Typography;

interface EditorAIConfigRow {
    id?: number;
    system_prompt: string;
    [k: string]: unknown;
}

interface EditorAiPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: {
        editor_ai_config?: EditorAIConfigRow[];
    };
}

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

/**
 * Configure tab body for Editor AI.
 *
 * The Astro static-site editor talks to Anthropic only — no fallback provider,
 * no per-row tabs. One singleton row in editor_ai_config holds the system
 * prompt; two system_settings hold the model id and temperature. Used by
 * src/editor/claude.service.ts when SUPER_ADMIN edits the website source from
 * the Editor AI page.
 */
export const EditorAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<EditorAiPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    const rows = editor.payload.configTableRows?.editor_ai_config ?? [];
    const row = rows[0];

    if (!row) {
        return (
            <Empty description="No editor_ai_config row — has the v1 baseline been seeded? The singleton row is required." />
        );
    }

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const updateRow = (patch: Partial<EditorAIConfigRow>) => {
        editor.setPayload((prev) => {
            const list = prev!.configTableRows.editor_ai_config ?? [];
            const next = list.map((r, i) => (i === 0 ? { ...r, ...patch } : r));
            return {
                ...prev!,
                configTableRows: { ...prev!.configTableRows, editor_ai_config: next },
            };
        });
    };

    const model = settings.EDITOR_AI_PRIMARY_MODEL ?? '';
    const temperature = Number(settings.EDITOR_AI_PRIMARY_TEMPERATURE ?? 0.3);

    const knownIds = new Set(anthropicModels.map((m) => m.id));
    const isOrphan = !!model && !knownIds.has(model);
    const modelOptions: Array<{ value: string; label: string }> = [];
    if (isOrphan) {
        modelOptions.push({
            value: model,
            label: `${model} (not in anthropic list — mismatch?)`,
        });
    }
    for (const m of anthropicModels) {
        modelOptions.push({ value: m.id, label: m.label ?? m.id });
    }

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Astro static-site editor. SUPER_ADMIN-only feature; talks to Anthropic
                    directly (no fallback provider). The system prompt is now a database
                    singleton — previously hardcoded in <code>claude.service.ts</code>.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card title="Anthropic model" size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={24} md={16}>
                        <Text strong>Model</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>EDITOR_AI_PRIMARY_MODEL</code> — pulled from the Models &amp;
                            Providers anthropic tab.
                        </Text>
                        <br />
                        <Select
                            style={{ width: '100%', marginTop: 4 }}
                            value={model}
                            onChange={(v) => setSetting('EDITOR_AI_PRIMARY_MODEL', v)}
                            options={modelOptions}
                            placeholder="Select model"
                            showSearch
                            optionFilterProp="label"
                        />
                    </Col>
                    <Col xs={24} md={8}>
                        <Text strong>Temperature</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>EDITOR_AI_PRIMARY_TEMPERATURE</code> — low values keep edits
                            deterministic and on-spec.
                        </Text>
                        <br />
                        <InputNumber
                            min={0}
                            max={2}
                            step={0.05}
                            style={{ width: 140, marginTop: 4 }}
                            value={temperature}
                            onChange={(v) =>
                                setSetting('EDITOR_AI_PRIMARY_TEMPERATURE', String(v ?? 0))
                            }
                        />
                    </Col>
                </Row>
            </Card>

            <Card title="System prompt (editor_ai_config singleton)" size="small">
                <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ marginBottom: 12 }}
                    message="Singleton row — only one editor_ai_config record"
                    description={
                        <>
                            The Astro editor sends every chat request with this prompt as the{' '}
                            <code>system</code> message. Editing here lets you tweak the JSON
                            output contract, file-handling rules, or persona without a code
                            deploy. <code>ClaudeService</code> falls back to a compiled-in copy
                            of this prompt only if the singleton row is somehow missing — that's
                            a "reseed v1 baseline" situation.
                        </>
                    }
                />
                <PromptEditor
                    consumerSubsystem="editor_ai"
                    label="system_prompt"
                    value={row.system_prompt ?? ''}
                    onChange={(v) => updateRow({ system_prompt: v })}
                    rows={20}
                />
                <Paragraph type="secondary" style={{ marginTop: 12 }}>
                    The editor expects every response to be a JSON object with{' '}
                    <code>edits</code>, <code>summary</code>, and <code>assistantMessage</code>{' '}
                    fields. Tweaks that loosen this contract will break parsing in{' '}
                    <code>ClaudeService.callClaude</code>; the service has a one-shot retry but
                    no third try.
                </Paragraph>
            </Card>
        </div>
    );
};
